import { useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { RTL_LOCALES } from '~/shared/constants/langConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'
import { i18nFormatters } from '~/shared/utils/i18nFormatters'

import { Styles as S } from './styles'

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

const getTargetDate = (lng: string) =>
  i18nFormatters.datetime(new Date(Date.now() + THIRTY_DAYS_MS), lng, {
    dateStyle: 'long',
  })

// viewBox is 330 wide; mirroring a point for the RTL layout is just
// `VIEWBOX_WIDTH - x` — same y, so the curve keeps its exact shape
const VIEWBOX_WIDTH = 330
const LEFT_MARGIN_CX = 8
const RIGHT_MARGIN_CX = 322
const HABIT_DOT_CX_LTR = 152
const LESSONS_DOT_CX_LTR = 289
const PILL_PADDING = 24
const TRIANGLE_HALF_WIDTH = 13

const CURVE_COLOR = '#0071FF'
const LESSONS_COLOR = '#FF8D00'
const DOT_RING = 'white'

const CURVE_PATH_LTR =
  'M8 166 C58 145 77 166 84 162 C115 163 154 121 154 122 C154 124 169 98 195 100 C238 103 260 55 289 42 C287 38 325 26 330 27'

// same curve, every x mirrored around the viewBox center (x' = 330 - x) —
// so it grows from the right margin toward the left, matching RTL reading
const CURVE_PATH_RTL =
  'M322 166 C272 145 253 166 246 162 C215 163 176 121 176 122 C176 124 161 98 135 100 C92 103 70 55 41 42 C43 38 5 26 0 27'

type IDotProps = {
  cx: number
  cy: number
  fill: string
}

const ChartDot = ({ cx, cy, fill }: IDotProps) => (
  <>
    <circle cx={cx} cy={cy} r="9" fill={DOT_RING} />
    <circle cx={cx} cy={cy} r="6.5" fill={fill} />
  </>
)

const ProofGraphAnatomy = ({ nextPagePath, pageId }: TPageProps) => {
  const { t, i18n } = useTranslation('anatomy')
  const habitTextRef = useRef<SVGTextElement>(null)
  const lessonsTextRef = useRef<SVGTextElement>(null)
  const [habitWidth, setHabitWidth] = useState(84)
  const [lessonsWidth, setLessonsWidth] = useState(100)

  const isRTL = RTL_LOCALES.includes(i18n.language)
  const curvePath = isRTL ? CURVE_PATH_RTL : CURVE_PATH_LTR
  const startDotCx = isRTL ? RIGHT_MARGIN_CX : LEFT_MARGIN_CX
  const week4Cx = isRTL ? LEFT_MARGIN_CX : RIGHT_MARGIN_CX
  const week4Anchor = isRTL ? 'start' : 'end'
  const habitDotCx = isRTL ? VIEWBOX_WIDTH - HABIT_DOT_CX_LTR : HABIT_DOT_CX_LTR
  const lessonsDotCx = isRTL
    ? VIEWBOX_WIDTH - LESSONS_DOT_CX_LTR
    : LESSONS_DOT_CX_LTR

  const habitText = t('onboarding.proofGraphAnatomy.habitFormed')
  const lessonsText = t('onboarding.proofGraphAnatomy.lessonsDone')

  useLayoutEffect(() => {
    if (habitTextRef.current) {
      setHabitWidth(
        Math.ceil(habitTextRef.current.getBBox().width) + PILL_PADDING,
      )
    }
    if (lessonsTextRef.current) {
      setLessonsWidth(
        Math.ceil(lessonsTextRef.current.getBBox().width) + PILL_PADDING,
      )
    }
  }, [habitText, lessonsText])

  const habitRectX = habitDotCx - habitWidth / 2
  const lessonsRectX = lessonsDotCx - lessonsWidth / 2

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: `We predict you'll feel confident in your anatomy knowledge`,
  })

  const targetDate = getTargetDate(i18n.language)

  return (
    <S.Root>
      <S.StyledContainer>
        <S.Title>
          <Trans
            ns="anatomy"
            i18nKey="onboarding.proofGraphAnatomy.title"
            values={{ date: targetDate }}
            components={{
              highlight: (
                <span style={{ color: '#8C67F6', fontWeight: 'inherit' }} />
              ),
            }}
          />
        </S.Title>

        <S.ChartCard>
          <svg
            viewBox="0 0 330 220"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              overflow: 'visible',
            }}
          >
            {/* Grid lines — 6 lines, 32px spacing (y=38→198) */}
            <line
              x1="8"
              y1="38"
              x2="322"
              y2="38"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="70"
              x2="322"
              y2="70"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="102"
              x2="322"
              y2="102"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="134"
              x2="322"
              y2="134"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="166"
              x2="322"
              y2="166"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="198"
              x2="322"
              y2="198"
              stroke="#E8ECF4"
              strokeWidth="1"
            />

            <S.AnimatedPath
              d={curvePath}
              pathLength="1"
              fill="none"
              stroke={CURVE_COLOR}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Dashed vertical line — from start dot down to bottom grid line */}
            <line
              x1={startDotCx}
              y1="168"
              x2={startDotCx}
              y2="198"
              stroke={CURVE_COLOR}
              strokeWidth="2.5"
              strokeDasharray="4 3"
            />

            {/* Start dot */}
            <ChartDot cx={startDotCx} cy={166} fill={CURVE_COLOR} />

            <S.HabitFormedGroup>
              {/* Habit formed dot */}
              <ChartDot cx={habitDotCx} cy={122} fill={CURVE_COLOR} />

              {/* Habit formed pill — centered above dot */}
              <rect
                x={habitRectX}
                y="81"
                width={habitWidth}
                height="22"
                rx="11"
                fill={CURVE_COLOR}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.300781 0C1.99516 0 3.61055 0.716408 4.74797 1.97228L9.9594 7.72645C11.5472 9.47958 14.3012 9.47958 15.889 7.72645L21.1004 1.97228C22.2378 0.716408 23.8532 0 25.5476 0H26L0 0H0.300781Z"
                fill={CURVE_COLOR}
                transform={`translate(${
                  habitDotCx - TRIANGLE_HALF_WIDTH
                }, 102)`}
              />
              <text
                ref={habitTextRef}
                x={habitDotCx}
                y="92"
                textAnchor="middle"
                dominantBaseline="middle"
                direction="ltr"
                fill="white"
                fontSize="10"
                style={{ fontWeight: 700 }}
              >
                {habitText}
              </text>
            </S.HabitFormedGroup>

            <S.LessonsGroup>
              {/* 30 lessons dot (orange) */}
              <ChartDot cx={lessonsDotCx} cy={42} fill={LESSONS_COLOR} />

              {/* 30 lessons pill — centered above dot */}
              <rect
                x={lessonsRectX}
                y="3"
                width={lessonsWidth}
                height="22"
                rx="11"
                fill={LESSONS_COLOR}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.300781 0C1.99516 0 3.61055 0.716408 4.74797 1.97228L9.9594 7.72645C11.5472 9.47958 14.3012 9.47958 15.889 7.72645L21.1004 1.97228C22.2378 0.716408 23.8532 0 25.5476 0H26L0 0H0.300781Z"
                fill={LESSONS_COLOR}
                transform={`translate(${
                  lessonsDotCx - TRIANGLE_HALF_WIDTH
                }, 24)`}
              />
              <text
                ref={lessonsTextRef}
                x={lessonsDotCx}
                y="14"
                textAnchor="middle"
                dominantBaseline="middle"
                direction="ltr"
                fill="white"
                fontSize="10"
                style={{ fontWeight: 700 }}
              >
                {lessonsText}
              </text>
            </S.LessonsGroup>

            {/* Week labels — 8px below bottom grid line (y=198)
                direction="ltr" pinned: text-anchor="end"/"start" flips
                meaning under an inherited RTL direction, which pushes the
                RTL-mirrored label past the chart's right edge. week4Cx/
                week4Anchor swap sides for RTL so the label stays glued to
                its (now mirrored) end-of-curve dot instead of overflowing */}
            <text
              x={startDotCx}
              y="206"
              textAnchor="middle"
              dominantBaseline="hanging"
              direction="ltr"
              fill={CURVE_COLOR}
              fontSize="12"
              letterSpacing="0.5"
              style={{ fontWeight: 600 }}
            >
              {t('onboarding.proofGraphAnatomy.week1')}
            </text>
            <text
              x={week4Cx}
              y="206"
              textAnchor={week4Anchor}
              dominantBaseline="hanging"
              direction="ltr"
              fill={CURVE_COLOR}
              fontSize="12"
              letterSpacing="0.5"
              style={{ fontWeight: 600 }}
            >
              {t('onboarding.proofGraphAnatomy.week4')}
            </text>
          </svg>
        </S.ChartCard>

        <S.InfoSection>
          <p>
            <Trans
              ns="anatomy"
              i18nKey="onboarding.proofGraphAnatomy.infoHabit"
            />
          </p>
          <p>
            <Trans
              ns="anatomy"
              i18nKey="onboarding.proofGraphAnatomy.infoTime"
            />
          </p>
        </S.InfoSection>

        <S.StyledButton type="button" onClick={() => handleContinue('')}>
          <Trans i18nKey="actions.continue" />
        </S.StyledButton>
      </S.StyledContainer>
    </S.Root>
  )
}

export default ProofGraphAnatomy
